import { useEffect, useRef, useState } from "react";

import {
  CountsError,
  requestSnapshot,
  requestStageSnapshot
} from "../api/client.js";

/**
 * Snapshot pill — sits next to the live indicator on the board (CR-02).
 * Click → POST /m/v1/snapshot/:eventId?token=… → render a transient
 * status pill on the result. Disabled while in flight and for a short
 * cool-down after success so double-clicks don't fire a second send.
 *
 * The button stamps `monitor-no-print` so the print stylesheet still hides
 * board chrome — the printed view is the headline number only.
 *
 * No rate limit on the server (CR-02 §2.7). The 3s post-success disable
 * is a UX guard, not a security guard.
 */

const COOLDOWN_MS = 3000;
const ERROR_HOLD_MS = 5000;

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "success"; recipientsCount: number; warning?: string }
  | { kind: "error"; message: string };

export function SnapshotButton({
  eventId,
  token,
  stageId
}: {
  eventId: string;
  token: string;
  stageId?: string;
}): JSX.Element {
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    };
  }, []);

  const scheduleReset = (ms: number): void => {
    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
    }
    resetTimer.current = setTimeout(() => {
      setStatus({ kind: "idle" });
    }, ms);
  };

  const onClick = async (): Promise<void> => {
    if (status.kind === "sending" || status.kind === "success") {
      return;
    }
    setStatus({ kind: "sending" });
    try {
      const res = stageId
        ? await requestStageSnapshot(stageId, token)
        : await requestSnapshot(eventId, token);
      setStatus({
        kind: "success",
        recipientsCount: res.recipients_count,
        warning: res.warning
      });
      scheduleReset(COOLDOWN_MS);
    } catch (err) {
      const message =
        err instanceof CountsError
          ? err.status === 401
            ? "Invalid link"
            : err.status === 404
              ? "Event not found"
              : err.code === "NETWORK"
                ? "Network error"
                : "Snapshot failed"
          : "Snapshot failed";
      setStatus({ kind: "error", message });
      scheduleReset(ERROR_HOLD_MS);
    }
  };

  return renderPill(status, onClick);
}

function renderPill(status: Status, onClick: () => Promise<void>): JSX.Element {
  const baseClasses =
    "monitor-no-print inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm transition-colors";

  if (status.kind === "sending") {
    return (
      <button
        type="button"
        disabled
        data-testid="snapshot-button"
        data-status="sending"
        className={`${baseClasses} cursor-wait bg-slate-900 text-slate-300`}
      >
        <span
          className="h-2 w-2 animate-pulse rounded-full bg-slate-400"
          aria-hidden
        />
        Sending…
      </button>
    );
  }

  if (status.kind === "success") {
    return (
      <button
        type="button"
        disabled
        data-testid="snapshot-button"
        data-status="success"
        className={`${baseClasses} bg-emerald-950/60 text-emerald-300`}
      >
        <span
          className="h-2 w-2 rounded-full bg-emerald-400"
          aria-hidden
        />
        {status.recipientsCount > 0
          ? `Sent · ${String(status.recipientsCount)}x`
          : status.warning ?? "Sent"}
      </button>
    );
  }

  if (status.kind === "error") {
    return (
      <button
        type="button"
        onClick={() => {
          void onClick();
        }}
        data-testid="snapshot-button"
        data-status="error"
        className={`${baseClasses} bg-rose-950/60 text-rose-300 hover:bg-rose-950/80`}
      >
        <span
          className="h-2 w-2 rounded-full bg-rose-400"
          aria-hidden
        />
        {status.message}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        void onClick();
      }}
      data-testid="snapshot-button"
      data-status="idle"
      className={`${baseClasses} bg-slate-900 text-slate-200 hover:bg-slate-800`}
    >
      <span
        className="h-2 w-2 rounded-full bg-slate-500"
        aria-hidden
      />
      Snapshot
    </button>
  );
}
